export const PAMI_COLORS = {
  H: "#6D28D9",
  E: "#CC6B4A",
  X: "#14B8C4",
  A: "#F0B429",
  C: "#6366F1",
  O: "#E0469E",
  motivation: "#2196F3",
} as const;

/**
 * Teintes des domaines assombries pour le TEXTE : en libellé sur blanc, le
 * cyan, le jaune ou l'orangé de `PAMI_COLORS` tombent sous 4,5:1. Les teintes
 * vives restent celles des aplats (roue, pastilles, carrés).
 */
export const PAMI_COLORS_TEXTE = {
  H: "#6D28D9",
  E: "#A4492B",
  X: "#0B7A83",
  A: "#8F6300",
  C: "#4F46E5",
  O: "#B42A7A",
  motivation: "#1565C0",
} as const;

/** Fond très clair de chaque domaine, pour l'encart d'une dimension. */
export const PAMI_COLORS_VOILE = {
  H: "#F3EEFC",
  E: "#FBF0EC",
  X: "#E9F7F8",
  A: "#FDF6E4",
  C: "#EFEFFD",
  O: "#FCEDF5",
  motivation: "#EAF4FE",
} as const;


export const HEXACO_FACTORS = [
  {
    key: "H",
    label: "Honnêteté-Humilité",
    color: PAMI_COLORS.H,
    description:
      "Tendance à se montrer sincère et authentique, en évitant de manipuler ou d'exploiter les autres pour son propre intérêt.",
    facets: [
      {
        prefix: "H1",
        code: "H1",
        label: "Sincérité",
        description:
          "Tendance à être authentique et à éviter la tromperie ou la manipulation dans ses interactions avec les autres.",
        interpretations: {
          bas: "Flatte les autres ou prétend les apprécier pour obtenir leurs faveurs.",
          haut: "Honnête et franc(he), évite de tromper ou de manipuler les autres pour obtenir un avantage.",
        },
      },
      {
        prefix: "H2",
        code: "H2",
        label: "Intégrité",
        description:
          "Tendance à éviter la fraude, la corruption, et les comportements malhonnêtes pour obtenir des avantages personnels.",
        interpretations: {
          bas: "Veut gagner par des moyens injustes ou déloyaux, ou en profitant des autres.",
          haut: "Agit de manière désintéressée et transparente, en veillant à ne pas tirer avantage des autres.",
        },
      },
    ],
  },
  {
    key: "E",
    label: "Émotivité",
    color: PAMI_COLORS.E,
    description:
      "Tendance à être calme, résilient(e), et capable de gérer le stress de manière efficace.",
    facets: [
      {
        prefix: "N1",
        code: "E1",
        label: "Calme",
        description:
          "Tendance à être détendu(e) et serein(e), même face à des situations stressantes.",
        interpretations: {
          bas: "Éprouve fréquemment de l'anxiété et de l'inquiétude, se sentant facilement submergé(e) par les situations stressantes.",
          haut: "Démontre une grande sérénité et une capacité à rester détendu(e) même face au stress et aux challenges.",
        },
      },
      {
        prefix: "N2",
        code: "E2",
        label: "Gestion du stress",
        description:
          "Tendance à maintenir son calme et à rester efficace sous pression.",
        interpretations: {
          bas: "A du mal à maintenir son efficacité lorsque la pression monte. Tend à être envahi(e) par ses émotions dans les situations difficiles.",
          haut: "Reste efficace et orienté(e) vers les solutions même sous forte pression. Sait gérer ses émotions sans les laisser interférer avec son jugement.",
        },
      },
    ],
  },
  {
    key: "X",
    label: "eXtraversion",
    color: PAMI_COLORS.X,
    description:
      "Tendance à rechercher activement la compagnie des autres, à se montrer énergique et à prendre plaisir dans les interactions sociales.",
    facets: [
      {
        prefix: "E1",
        code: "X1",
        label: "Affirmation de soi",
        description:
          "Tendance à être confiant(e), direct(e), et capable de prendre l'initiative dans les situations sociales ou professionnelles.",
        interpretations: {
          bas: "Tend à être réservé(e) et à éviter de prendre les devants ou d'exprimer ses opinions de manière directe.",
          haut: "Exprime ses opinions avec confiance, prend l'initiative et assume volontiers des rôles de leadership.",
        },
      },
      {
        prefix: "E2",
        code: "X2",
        label: "Sociabilité",
        description:
          "Tendance à apprécier la compagnie d'autrui et les interactions sociales.",
        interpretations: {
          bas: "Préfère la solitude ou les interactions en petits groupes, et se sent souvent plus à l'aise dans des environnements calmes ou privés.",
          haut: "Aime être entouré(e) de personnes, recherche les interactions sociales et tire son énergie de la présence des autres.",
        },
      },
    ],
  },
  {
    key: "A",
    label: "Agréabilité",
    color: PAMI_COLORS.A,
    description:
      "Tendance à être conciliant(e) et indulgent(e), en cherchant à maintenir des relations harmonieuses et à éviter les conflits.",
    facets: [
      {
        prefix: "A1",
        code: "A1",
        label: "Gestion des conflits",
        description:
          "Tendance à aborder les désaccords et les tensions interpersonnelles de manière constructive.",
        interpretations: {
          bas: "A du mal à faire face aux désaccords et aux tensions. Tend soit à les éviter au détriment de ses propres besoins, soit à y réagir de façon disproportionnée.",
          haut: "Aborde les désaccords avec assurance et bienveillance, en cherchant des solutions qui respectent les besoins de chacun.",
        },
      },
      {
        prefix: "A2",
        code: "A2",
        label: "Altruisme",
        description:
          "Tendance à être disposé(e) à aider les autres et à se montrer bienveillant(e).",
        interpretations: {
          bas: "Centré(e) sur ses propres intérêts et moins soucieux(se) des autres.",
          haut: "Montre une grande préoccupation pour les autres, avec une volonté constante d'aider et de soutenir ceux qui en ont besoin.",
        },
      },
    ],
  },
  {
    key: "C",
    label: "Conscienciosité",
    color: PAMI_COLORS.C,
    description:
      "Tendance à être organisé(e) et persévérant(e) dans la réalisation de ses tâches, en poursuivant ses objectifs avec rigueur et discipline.",
    facets: [
      {
        prefix: "C1",
        code: "C1",
        label: "Autodiscipline",
        description:
          "Tendance à être capable de se concentrer sur son travail et de persévérer jusqu'à ce qu'il soit terminé.",
        interpretations: {
          bas: "Éprouve des difficultés à rester concentré(e) sur son travail et à le mener à bien, a tendance à se laisser distraire et à procrastiner.",
          haut: "Démontre une forte capacité à se concentrer et à terminer son travail avec persévérance, même face aux distractions.",
        },
      },
      {
        prefix: "C2",
        code: "C2",
        label: "Professionnalisme",
        description:
          "Tendance à s'acquitter de ses responsabilités avec sérieux et rigueur, en respectant les consignes, les délais et les engagements pris.",
        interpretations: {
          bas: "Peine à s'imposer la discipline nécessaire pour honorer ses engagements. Peut négliger les consignes ou les délais.",
          haut: "S'acquitte de ses responsabilités avec constance et rigueur, quel que soit son niveau de motivation.",
        },
      },
    ],
  },
  {
    key: "O",
    label: "Ouverture",
    color: PAMI_COLORS.O,
    description:
      "Tendance à explorer de nouvelles idées, à apprécier la diversité des expériences, et à s'engager dans des activités intellectuelles ou créatives.",
    facets: [
      {
        prefix: "O1",
        code: "O1",
        label: "Intellect",
        description:
          "Tendance à avoir une forte curiosité intellectuelle et un désir d'explorer de nouvelles perspectives.",
        interpretations: {
          bas: "Montre peu d'intérêt pour les idées nouvelles ou les concepts abstraits, préfère les choses concrètes et pratiques.",
          haut: "Démontre une forte curiosité intellectuelle et un intérêt prononcé pour les idées nouvelles, les discussions profondes et les questions complexes.",
        },
      },
      {
        prefix: "O2",
        code: "O2",
        label: "Créativité",
        description:
          "Tendance à générer des idées nouvelles et originales, en laissant libre cours à son imagination.",
        interpretations: {
          bas: "Peu attiré(e) par l'exploration d'idées nouvelles et la création. Préfère s'appuyer sur des approches éprouvées.",
          haut: "Génère facilement des idées originales et éprouve un réel plaisir à imaginer et à créer.",
        },
      },
    ],
  },
];

export const MOTIVATION_DIMS = [
  {
    prefix: "Auto",
    label: "Autonomie",
    description:
      "Besoin de disposer d'une liberté d'action dans l'organisation et la réalisation de son travail.",
    interpretations: {
      bas: "S'accommode d'un cadre structuré et d'un encadrement proche, sans ressentir le besoin de maîtriser ses méthodes ou son organisation.",
      haut: "Éprouve un fort besoin de liberté dans la conduite de son travail et peut se sentir freiné(e) dans des environnements très hiérarchisés.",
    },
  },
  {
    prefix: "Equi",
    label: "Équilibre vie pro/perso",
    description:
      "Besoin de préserver une frontière claire entre vie professionnelle et vie personnelle.",
    interpretations: {
      bas: "Accepte volontiers des horaires contraignants ou une forte disponibilité sans que cela affecte sa motivation.",
      haut: "Place la qualité de vie au cœur de ses priorités et a besoin d'une organisation du travail qui préserve du temps pour sa vie personnelle.",
    },
  },
  {
    prefix: "Manag",
    label: "Qualité du management",
    description:
      "Besoin d'évoluer sous un encadrement bienveillant, qui reconnaît les contributions individuelles et favorise un dialogue ouvert.",
    interpretations: {
      bas: "Travaille efficacement quelle que soit la qualité de la relation hiérarchique, sans attendre de reconnaissance ou d'accompagnement particulier.",
      haut: "A besoin de se sentir soutenu(e) et reconnu(e) par sa hiérarchie pour s'investir pleinement.",
    },
  },
  {
    prefix: "Rea",
    label: "Réalisation de soi",
    description:
      "Besoin d'accomplissement professionnel, traduit par le désir de valoriser ses compétences et d'atteindre des objectifs ambitieux.",
    interpretations: {
      bas: "Accomplit son travail sans chercher particulièrement à se dépasser ou à obtenir une validation de ses compétences.",
      haut: "Tire une grande satisfaction de la réussite de ses missions et a besoin de se sentir en progression.",
    },
  },
  {
    prefix: "Rev",
    label: "Revenus",
    description:
      "Besoin d'une rémunération perçue comme juste et évolutive, en adéquation avec ses compétences et ses ambitions.",
    interpretations: {
      bas: "La dimension salariale n'est pas un moteur central de motivation.",
      haut: "La rémunération est un critère déterminant de motivation. Attache de l'importance aux perspectives d'évolution salariale.",
    },
  },
  {
    prefix: "Secu",
    label: "Sécurité professionnelle",
    description:
      "Besoin de stabilité et de prévisibilité dans sa situation professionnelle.",
    interpretations: {
      bas: "Tolère bien l'incertitude et les environnements changeants, voire les recherche.",
      haut: "La stabilité de l'emploi est une condition importante de son bien-être. Privilégie les structures établies.",
    },
  },
  {
    prefix: "Statut",
    label: "Statut social",
    description:
      "Besoin que son activité professionnelle lui confère une reconnaissance sociale, une visibilité et un prestige.",
    interpretations: {
      bas: "Le prestige de l'employeur ou la reconnaissance sociale associée à son poste n'influencent pas sa motivation professionnelle.",
      haut: "Être associé(e) à une organisation reconnue et occuper un poste valorisé socialement constituent des sources de motivation significatives.",
    },
  },
  {
    prefix: "Var",
    label: "Variété des tâches",
    description:
      "Besoin de diversité dans les activités et les projets professionnels, afin d'éviter la routine.",
    interpretations: {
      bas: "S'épanouit dans un périmètre de tâches stable et maîtrisé. La routine ne génère pas d'ennui.",
      haut: "A besoin de diversité et de renouvellement dans ses missions pour rester motivé(e).",
    },
  },
];

export const TRAIT_NAME_TO_PREFIX: Record<string, string> = {
  Sincérité: "H1",
  Intégrité: "H2",
  Calme: "N1",
  "Gestion du stress": "N2",
  "Affirmation de soi": "E1",
  Sociabilité: "E2",
  "Gestion des conflits": "A1",
  Altruisme: "A2",
  Autodiscipline: "C1",
  Professionnalisme: "C2",
  Intellect: "O1",
  Créativité: "O2",
  Autonomie: "Auto",
  Equilibre: "Equi",
  Management: "Manag",
  Réalisation: "Rea",
  Revenus: "Rev",
  "Sécurité pro": "Secu",
  "Statut social": "Statut",
  Variété: "Var",
};
